import { getSupabase } from "../auth/supabaseClient";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  try {
    return await navigator.serviceWorker.register("/sw-push.js");
  } catch {
    return null;
  }
}

export const pushService = {
  async isSupported(): Promise<boolean> {
    return "serviceWorker" in navigator && "PushManager" in window && VAPID_PUBLIC_KEY != null;
  },

  async isSubscribed(): Promise<boolean> {
    const reg = await getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return sub != null;
  },

  async subscribe(userId: string): Promise<boolean> {
    if (!VAPID_PUBLIC_KEY) return false;
    const reg = await getRegistration();
    if (!reg) return false;

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return false;

    try {
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });

      const json = sub.toJSON();
      const { error } = await getSupabase().from("push_subscriptions").upsert(
        {
          user_id: userId,
          endpoint: json.endpoint!,
          p256dh: json.keys!.p256dh!,
          auth: json.keys!.auth!,
        },
        { onConflict: "user_id,endpoint" }
      );

      if (error) {
        console.error("[push] save subscription failed:", error.message);
        return false;
      }

      // Mark push enabled on profile
      await getSupabase().from("profiles").update({ push_enabled: true }).eq("id", userId);
      return true;
    } catch (err) {
      console.error("[push] subscribe failed:", err);
      return false;
    }
  },

  async unsubscribe(userId: string): Promise<void> {
    const reg = await getRegistration();
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await sub.unsubscribe();
        await getSupabase()
          .from("push_subscriptions")
          .delete()
          .eq("user_id", userId)
          .eq("endpoint", sub.endpoint);
      }
    }
    await getSupabase().from("profiles").update({ push_enabled: false }).eq("id", userId);
  },
};
